import type { AuthResponse } from "../types/auth";

const BASE_URL = import.meta.env.VITE_API_URL || "/api";

let accessToken: string | null = null;
let refreshRequest: Promise<AuthResponse> | null = null;
const accessTokenListeners = new Set<() => void>();

export function setAccessToken(token: string | null): void {
  if (accessToken === token) return;
  accessToken = token;
  accessTokenListeners.forEach((listener) => listener());
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function subscribeAccessToken(listener: () => void): () => void {
  accessTokenListeners.add(listener);
  return () => {
    accessTokenListeners.delete(listener);
  };
}

/**
 * Rotating refresh tokens are single-use, so every browser tab must have at
 * most one refresh request in flight. React StrictMode intentionally runs
 * mount effects twice in development, and multiple failed API requests can
 * also race into the interceptor. Sharing this promise keeps both paths from
 * submitting the same cookie twice and revoking an otherwise valid session.
 */
export function refreshSession(): Promise<AuthResponse> {
  if (refreshRequest) return refreshRequest;

  refreshRequest = fetch(`${BASE_URL}/auth/refresh`, {
    method: "POST",
    credentials: "include",
  })
    .then(async (response) => {
      if (!response.ok) throw await parseError(response);
      const body = (await response.json()) as { data: AuthResponse };
      return body.data;
    })
    .finally(() => {
      refreshRequest = null;
    });

  return refreshRequest;
}

/**
 * Payload delivered to an {@link AuthRotationListener} whenever the API client
 * acquires a freshly-issued access token via /auth/refresh. Note: the
 * Family membership commands perform rotation in `api-family.ts` and call the
 * listener directly. They do not funnel through this interceptor.
 */
export interface AuthRotationEvent {
  access_token: string;
  expires_in: number;
  user?: AuthResponse["user"];
}

export type AuthRotationListener = (event: AuthRotationEvent) => void;
export type AuthInvalidatedListener = () => void;

let onAuthRotation: AuthRotationListener | null = null;
let onAuthInvalidated: AuthInvalidatedListener | null = null;

/**
 * Fires the rotation listener from outside the interceptor. Used by
 * `api-family.ts` after membership-changing commands to broadcast the fresh
 * access token returned in the response body, so AuthProvider can sync
 * React state without an extra /auth/refresh round-trip.
 */
export function notifyAuthRotation(event: AuthRotationEvent): void {
  onAuthRotation?.(event);
}

/**
 * Registers a single listener invoked whenever the API client rotates the
 * in-memory access token as a result of a transparent refresh inside an
 * interceptor (401 retry, 403 `family_context_required` retry). The listener
 * is responsible for keeping React state (user, refresh-timer, auth hint) in
 * sync. Returns an unsubscribe function so React effects can deregister on
 * unmount.
 */
export function setAuthRotationListener(
  listener: AuthRotationListener | null
): () => void {
  if (
    import.meta.env.DEV &&
    onAuthRotation !== null &&
    listener !== null &&
    onAuthRotation !== listener
  ) {
    console.warn(
      "[api-client] setAuthRotationListener overwrote an existing listener — only the last one will receive rotation events."
    );
  }
  onAuthRotation = listener;
  return () => {
    if (onAuthRotation === listener) {
      onAuthRotation = null;
    }
  };
}

/**
 * Registers the listener invoked when the API client detects the session is
 * irrecoverable (refresh failed inside a 401/403 retry). The listener should
 * clear local auth state and redirect to /login.
 */
export function setAuthInvalidatedListener(
  listener: AuthInvalidatedListener | null
): () => void {
  if (
    import.meta.env.DEV &&
    onAuthInvalidated !== null &&
    listener !== null &&
    onAuthInvalidated !== listener
  ) {
    console.warn(
      "[api-client] setAuthInvalidatedListener overwrote an existing listener — only the last one will be notified on session invalidation."
    );
  }
  onAuthInvalidated = listener;
  return () => {
    if (onAuthInvalidated === listener) {
      onAuthInvalidated = null;
    }
  };
}

interface RequestOptions extends RequestInit {
  /**
   * Internal guard: marks a request as a post-refresh retry so the
   * interceptors never enter a recursive refresh loop. Always undefined on
   * the first call from consumer code.
   */
  __retried?: boolean;
}

async function request<T>(
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const { __retried, ...fetchOptions } = options;
  const headers = new Headers(fetchOptions.headers);
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }
  if (!headers.has("Content-Type") && fetchOptions.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...fetchOptions,
    headers,
    credentials: "include",
  });

  const isAuthEndpoint =
    path.startsWith("/auth/refresh") ||
    path.startsWith("/auth/register") ||
    path.startsWith("/auth/login") ||
    path.startsWith("/auth/vk/callback") ||
    path.startsWith("/auth/google/callback");

  // 401: stale access token. Try refresh once, then retry the request.
  if (response.status === 401 && !isAuthEndpoint && !__retried) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      return request<T>(path, { ...options, __retried: true });
    }
    setAccessToken(null);
    onAuthInvalidated?.();
    throw new ApiError(401, "UNAUTHORIZED", "Session expired");
  }

  // 403 family_context_required: token is missing the `fam` claim (FG-90).
  // Typically happens to clients that authenticated before joining a family
  // and never rotated their token. We attempt a single transparent refresh
  // to pick up the freshly-issued `fam` claim, then retry the request once.
  if (response.status === 403 && !isAuthEndpoint && !__retried) {
    const parsed = await peekErrorBody(response);
    if (parsed?.error?.code === FAMILY_CONTEXT_REQUIRED_CODE) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        return request<T>(path, { ...options, __retried: true });
      }
      setAccessToken(null);
      onAuthInvalidated?.();
      throw new ApiError(
        403,
        FAMILY_CONTEXT_REQUIRED_CODE,
        "Family context required"
      );
    }
    // Fall through to the generic !response.ok branch below.
    throw new ApiError(
      response.status,
      parsed?.error?.code ?? "FORBIDDEN",
      parsed?.error?.message ?? response.statusText
    );
  }

  if (!response.ok) {
    throw await parseError(response);
  }

  if (response.status === httpNoContent) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

const httpNoContent = 204;

async function tryRefresh(): Promise<boolean> {
  try {
    const data = await refreshSession();
    setAccessToken(data.access_token);
    onAuthRotation?.({
      access_token: data.access_token,
      expires_in: data.expires_in,
      user: data.user,
    });
    return true;
  } catch {
    return false;
  }
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as ApiErrorBody;
    return new ApiError(
      response.status,
      body.error?.code ?? "UNKNOWN",
      body.error?.message ?? response.statusText
    );
  } catch {
    return new ApiError(response.status, "UNKNOWN", response.statusText);
  }
}

/**
 * Reads the response body once and returns the parsed error envelope (or
 * null if the body is not JSON). Consumed by the 403 interceptor so it can
 * inspect `error.code` without losing the body for the fallthrough path.
 */
async function peekErrorBody(response: Response): Promise<ApiErrorBody | null> {
  try {
    return (await response.json()) as ApiErrorBody;
  } catch {
    return null;
  }
}

/**
 * Machine-readable error code returned by the backend
 * FamilyContextMiddleware when the caller's JWT lacks the `fam` claim
 * (FG-90). Exported so feature code can render specific UX if desired,
 * but the interceptor handles the recovery path transparently.
 */
export const FAMILY_CONTEXT_REQUIRED_CODE = "family_context_required";

export const apiClient = {
  get<T>(path: string): Promise<T> {
    return request<T>(path, { method: "GET" });
  },

  post<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return request<T>(path, {
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  },

  patch<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  put<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  delete<T>(path: string): Promise<T> {
    return request<T>(path, { method: "DELETE" });
  },
};

interface ApiErrorBody {
  error?: {
    code: string;
    message: string;
  };
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}
